import { Flex } from "~/components/Flex";
import { Text } from "../Text";
import { stepperMobileStyles, getMobileStepStyles } from "./stylesMobile";

type StepperProgressMobileProps = {
  steps: string[];
  activeStep: number;
  allowNavigation?: boolean;
  showStepNumber?: boolean;
  showAllSteps?: boolean;
  onStepClick?: (stepIndex: number) => void;
  canNavigateToStep?: (stepIndex: number, currentStep: number) => boolean;
};

export const StepperProgressMobile = ({
  steps,
  activeStep,
  allowNavigation = true,
  showStepNumber = false,
  showAllSteps = true,
  onStepClick,
  canNavigateToStep,
}: StepperProgressMobileProps) => {
  const handleStepClick = (index: number) => {
    if (allowNavigation && onStepClick) {
      if (canNavigateToStep && !canNavigateToStep(index, activeStep)) {
        return;
      }
      onStepClick(index);
    }
  };

  const totalSteps = steps.length;
  const segmentWidth = totalSteps > 1 ? 100 / (totalSteps - 1) : 100;

  return (
    <Flex direction="column" gap="2" css={{ width: "100%" }}>
      {showAllSteps && (
        <Flex css={stepperMobileStyles.titlesContainer}>
          <Text
            css={{
              ...stepperMobileStyles.stepText,
              visibility: "hidden",
              pointerEvents: "none",
            }}
          >
            {steps[0] || "Step"}
          </Text>
          {steps.map((label, index) => {
            const stepStyles = getMobileStepStyles(index, activeStep, allowNavigation);
            const titleText = showStepNumber ? `${index + 1}. ${label}` : label;
            const leftPosition =
              totalSteps <= 1
                ? "50%"
                : index === 0
                  ? "6px"
                  : index === totalSteps - 1
                    ? "calc(100% - 6px)"
                    : `calc(6px + (100% - 12px) * ${index / (totalSteps - 1)})`;

            return (
              <Flex
                key={`title-${label}`}
                css={{
                  ...stepperMobileStyles.titleWrapper,
                  left: leftPosition,
                  cursor: stepStyles.cursor,
                }}
                onClick={() => handleStepClick(index)}
              >
                <Text
                  css={{
                    ...stepperMobileStyles.stepText,
                    color: stepStyles.textColor,
                    fontWeight: stepStyles.fontWeight,
                  }}
                >
                  {titleText}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      )}

      <Flex css={stepperMobileStyles.dotsContainer}>
        <Flex css={stepperMobileStyles.trackContainer}>
          {steps.map((_, index) => {
            if (index >= totalSteps - 1) return null;
            const stepStyles = getMobileStepStyles(index, activeStep, allowNavigation);
            return (
              <Flex
                key={`connector-${index}`}
                css={{
                  ...stepperMobileStyles.connectorSegment,
                  left: `${index * segmentWidth}%`,
                  width: `${segmentWidth}%`,
                  background: stepStyles.connectorRightBackground,
                }}
              />
            );
          })}
        </Flex>

        {steps.map((label, index) => {
          const stepStyles = getMobileStepStyles(index, activeStep, allowNavigation);
          return (
            <Flex
              key={`dot-${label}`}
              align="center"
              justify="center"
              css={{
                ...stepperMobileStyles.stepIndicatorWrapper,
                cursor: stepStyles.cursor,
              }}
              onClick={() => handleStepClick(index)}
            >
              <Flex
                align="center"
                justify="center"
                css={{
                  ...stepperMobileStyles.stepIndicator,
                  border: `2px solid ${stepStyles.indicatorBorder}`,
                  background: stepStyles.indicatorBackground,
                }}
              />
            </Flex>
          );
        })}
      </Flex>

      {!showAllSteps && (
        <Text size="2" fw="600" variant="neutral-600">
          {showStepNumber ? `Etapa ${activeStep + 1} - ` : ""}
          {steps[activeStep]}
        </Text>
      )}
    </Flex>
  );
};
